// ===========================================
// HTML Sanitization Utility
// Prevents XSS attacks from user/external content
// ===========================================

import DOMPurify from 'dompurify';

// Configure DOMPurify with strict settings
const purifyConfig = {
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'span', 'div', 'p', 'br', 'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'img',
  ] as string[],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'target'] as string[],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input', 'button'] as string[],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'] as string[],
  RETURN_TRUSTED_TYPE: false as const,
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this for any user-provided or external content
 */
export function sanitizeHTML(dirty: string): string {
  return DOMPurify.sanitize(dirty, purifyConfig) as string;
}

/**
 * Sanitize and set innerHTML safely
 */
export function setInnerHTMLSafe(element: HTMLElement, html: string): void {
  element.innerHTML = DOMPurify.sanitize(html, purifyConfig) as string;
}

/**
 * Create element with sanitized content
 */
export function createElementWithSafeHTML(tag: string, html: string): HTMLElement {
  const element = document.createElement(tag);
  element.innerHTML = DOMPurify.sanitize(html, purifyConfig) as string;
  return element;
}

/**
 * Escape text for safe display (no HTML parsing)
 * Use this when you just want to display text content
 */
export function escapeText(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Safe way to set text content (preferred over innerHTML when possible)
 */
export function setTextSafe(element: HTMLElement, text: string): void {
  element.textContent = text;
}

export default {
  sanitizeHTML,
  setInnerHTMLSafe,
  createElementWithSafeHTML,
  escapeText,
  setTextSafe,
};
